import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const WeatherWidget = ({ location = 'São Paulo, SP' }) => {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Mock weather data - in real app, this would come from weather API
  useEffect(() => {
    const mockWeatherData = {
      current: {
        temperature: 24,
        condition: 'Parcialmente nublado',
        icon: 'Cloud',
        humidity: 65,
        windSpeed: 12,
        rainChance: 20,
        uvIndex: 6,
        feelsLike: 26
      },
      forecast: [
        {
          day: 'Hoje',
          date: new Date()?.toISOString()?.split('T')?.[0],
          high: 26,
          low: 18,
          condition: 'Parcialmente nublado',
          icon: 'Cloud',
          rainChance: 20
        },
        {
          day: 'Amanhã',
          date: new Date(Date.now() + 86400000)?.toISOString()?.split('T')?.[0],
          high: 28,
          low: 20,
          condition: 'Ensolarado',
          icon: 'Sun',
          rainChance: 5
        },
        {
          day: 'Sábado',
          date: new Date(Date.now() + 172800000)?.toISOString()?.split('T')?.[0],
          high: 25,
          low: 19,
          condition: 'Chuva leve',
          icon: 'CloudRain',
          rainChance: 70
        },
        {
          day: 'Domingo',
          date: new Date(Date.now() + 259200000)?.toISOString()?.split('T')?.[0],
          high: 23,
          low: 17,
          condition: 'Nublado',
          icon: 'CloudDrizzle',
          rainChance: 40
        }
      ]
    };

    setWeather(mockWeatherData?.current);
    setForecast(mockWeatherData?.forecast);
  }, []);

  const getWeatherIcon = (iconName) => {
    return iconName;
  };

  const getWeatherColor = (condition) => {
    if (condition?.includes('Sol') || condition?.includes('Ensolarado')) return 'text-warning';
    if (condition?.includes('Chuva') || condition?.includes('Chuvoso')) return 'text-primary';
    if (condition?.includes('Nublado') || condition?.includes('Nuvem')) return 'text-muted-foreground';
    return 'text-muted-foreground';
  };

  const getWeatherBg = (condition) => {
    if (condition?.includes('Sol') || condition?.includes('Ensolarado')) return 'bg-warning/10';
    if (condition?.includes('Chuva') || condition?.includes('Chuvoso')) return 'bg-primary/10';
    if (condition?.includes('Nublado') || condition?.includes('Nuvem')) return 'bg-muted/50';
    return 'bg-muted/50';
  };

  const getRainChanceColor = (chance) => {
    if (chance >= 70) return 'text-error';
    if (chance >= 40) return 'text-warning';
    return 'text-success';
  };

  const getMatchCondition = (rainChance) => {
    if (rainChance >= 70) return { text: 'Não recomendado', color: 'text-error' };
    if (rainChance >= 40) return { text: 'Atenção ao tempo', color: 'text-warning' };
    return { text: 'Ideal para pelada', color: 'text-success' };
  };

  if (!weather) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${getWeatherBg(weather?.condition)}`}>
              <Icon 
                name={getWeatherIcon(weather?.icon)} 
                size={20} 
                className={getWeatherColor(weather?.condition)} 
              />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-card-foreground">
                Clima
              </h2>
              <p className="text-sm text-muted-foreground">
                {location}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-muted/50 rounded-lg transition-standard"
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              className="text-muted-foreground" 
            />
          </button>
        </div>
      </div>
      {/* Current Weather */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-3xl font-bold text-card-foreground">
              {weather?.temperature}°C
            </div>
            <div className="text-sm text-muted-foreground">
              {weather?.condition}
            </div>
            <div className="text-xs text-muted-foreground">
              Sensação: {weather?.feelsLike}°C
            </div>
          </div>
          
          <div className="text-right">
            <div className={`flex items-center space-x-1 ${getRainChanceColor(weather?.rainChance)} mb-1`}>
              <Icon name="CloudRain" size={14} />
              <span className="text-sm font-medium">
                {weather?.rainChance}%
              </span>
            </div>
            <div className={`text-xs font-medium ${getMatchCondition(weather?.rainChance)?.color}`}>
              {getMatchCondition(weather?.rainChance)?.text}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {!isExpanded && (
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="flex items-center justify-center space-x-1 text-muted-foreground mb-1">
                <Icon name="Droplets" size={14} />
                <span className="text-xs">Umidade</span>
              </div>
              <div className="text-sm font-medium text-card-foreground">
                {weather?.humidity}%
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-center space-x-1 text-muted-foreground mb-1">
                <Icon name="Wind" size={14} />
                <span className="text-xs">Vento</span>
              </div>
              <div className="text-sm font-medium text-card-foreground">
                {weather?.windSpeed}km/h
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-center space-x-1 text-muted-foreground mb-1">
                <Icon name="Sun" size={14} />
                <span className="text-xs">UV</span>
              </div>
              <div className="text-sm font-medium text-card-foreground">
                {weather?.uvIndex}
              </div>
            </div>
          </div>
        )}

        {/* Expanded Details */}
        {isExpanded && (
          <div className="space-y-4">
            {/* Detailed Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                <Icon name="Droplets" size={16} className="text-primary" />
                <div>
                  <div className="text-sm font-medium text-card-foreground">
                    {weather?.humidity}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Umidade
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                <Icon name="Wind" size={16} className="text-secondary" />
                <div>
                  <div className="text-sm font-medium text-card-foreground">
                    {weather?.windSpeed}km/h
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Vento
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                <Icon name="Sun" size={16} className="text-warning" />
                <div>
                  <div className="text-sm font-medium text-card-foreground">
                    {weather?.uvIndex}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Índice UV
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                <Icon name="CloudRain" size={16} className="text-primary" />
                <div>
                  <div className="text-sm font-medium text-card-foreground">
                    {weather?.rainChance}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Chuva
                  </div>
                </div>
              </div>
            </div>

            {/* 4-Day Forecast */}
            <div>
              <h3 className="font-heading font-semibold text-card-foreground mb-3">
                Próximos Dias
              </h3>
              <div className="space-y-2">
                {forecast?.map((day, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon 
                        name={getWeatherIcon(day?.icon)} 
                        size={16} 
                        className={getWeatherColor(day?.condition)} 
                      />
                      <div>
                        <div className="text-sm font-medium text-card-foreground">
                          {day?.day}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {day?.condition}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center space-x-1 ${getRainChanceColor(day?.rainChance)}`}>
                        <Icon name="CloudRain" size={12} />
                        <span className="text-xs">
                          {day?.rainChance}%
                        </span>
                      </div>
                      <div className="text-sm font-medium text-card-foreground">
                        {day?.high}°/{day?.low}°
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherWidget;